import { type RefObject, useLayoutEffect, useRef, useState } from "react";

/**
 * Measures whether the ref'd element's text is currently clipped by
 * `truncate`/`overflow-hidden` — `scrollWidth` (the content's full width)
 * exceeding `clientWidth` (the box it's squeezed into). Re-measures whenever
 * the element resizes (responsive/fluid widths) or `text` changes (the same
 * element rendering a different string, e.g. a row being recycled by key).
 *
 * Also re-measures once `document.fonts.ready` resolves: a webfont swap
 * (e.g. the `font-display: swap` fallback-to-Inter transition) changes glyph
 * widths — and so `scrollWidth` — without changing the element's box, so
 * `ResizeObserver` alone never catches it.
 *
 * Consumers use this to show a tooltip only for titles that actually
 * overflow, rather than on every hover regardless of whether there's
 * anything hidden to reveal.
 */
export function useIsTruncated<T extends HTMLElement>(
  text: string,
): [RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: text isn't read in the body, but it's the trigger that must force a re-measure when the element's content changes without changing size (e.g. a recycled node).
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }

    let cancelled = false;
    const measure = () => {
      if (cancelled) return;
      setIsTruncated(node.scrollWidth > node.clientWidth);
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    void document.fonts.ready.then(measure);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [text]);

  return [ref, isTruncated];
}
